import styles from './BankingOfferModule.module.scss';

import React, { FC, useRef, useState } from 'react';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { useTranslation } from 'react-i18next';
import { FormControl, FormControlLabel, Checkbox } from '@material-ui/core';
import cx from 'classnames';

import { AppType, toEntry } from 'services/cms/typesUtil';
import { FlatEntry } from 'services/cms/flattenContentful';
import { ButtonLinkV2 } from 'components/Button/ButtonLinkV2';
import { FiIntegration, LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { ISwiperClass } from 'components/CarouselModule/CarouselModule';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { SubjectMedia } from 'components/SubjectMedia/SubjectMedia';
import { TextV2 } from 'components/TextV2/TextV2';
import { VisuallyHidden } from 'components/VisuallyHidden/VisuallyHidden';
import { StructuredModal } from 'components/StructuredModal/StructuredModal';
import { useFiMerchant } from 'hooks/useFiMerchant';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { useHistory } from 'react-router-dom';
import { BANKING_OFFER_MODULE_TEST_ID } from './BankingOfferModule.const';

export type BankingOfferModuleProps = AppType.Component<FlatEntry<FiIntegration>>;

export const BankingOfferModule: FC<BankingOfferModuleProps> = ({
  _attributes,
  items = [],
  link,
  checkboxLabel,
  feesInformation,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isChecked, setIsChecked] = useState(false);
  const { t } = useTranslation();
  const apiRef = useRef<{ swiper: ISwiperClass } | null>(null);
  const history = useHistory();
  const { hasFailedFetchingMerchantProspectData, setHasFailedFetchingMerchantProspectData } = useFiMerchant();

  const swiperProps: SwiperProps = {
    speed: 500,
    slidesPerView: 1,
    watchSlidesProgress: true,
  };

  const closeModalAndRedirect = () => {
    setHasFailedFetchingMerchantProspectData(false);
    history.push(redesignV2RoutePath.PURCHASE + '/banking-offer/?eventType=closeWebview');
  };

  return (
    <>
      <section className={styles.fiIntegrationWrapper} {..._attributes}>
        <div className={styles.carouselContainer}>
          <Swiper
            ref={apiRef}
            resizeObserver
            onSlideChange={({ realIndex }: { realIndex: number }) => setCurrentIndex(realIndex)}
            className={styles.carousel}
            {...swiperProps}
          >
            {items.map((media, index) => (
              <SwiperSlide key={index} className={styles.carouselSlide}>
                <SubjectMedia entry={media} className={styles.media} playWhenInView loop />
                <div className={styles.carouselContent}>
                  <TextV2 variant="h2-semibold" value={media?.title} />
                  <RichBlurb
                    className={styles.carouselBlurb}
                    textVariant="body-small"
                    document={media?.richBlurb}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <nav>
            <ul className={styles.bullets}>
              {items.map((_, index) => (
                <li key={index}>
                  <button
                    className={cx(styles.bullet, currentIndex === index && styles.active)}
                    onClick={() => apiRef.current?.swiper.slideTo(index)}
                  >
                    <VisuallyHidden>{t('go-to-slide', { slideNumber: index + 1 })}</VisuallyHidden>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
        <div className={styles.ctaContainer}>
          <FormControl fullWidth>
            <FormControlLabel
              className={styles.checkboxLabel}
              control={
                <Checkbox
                  data-testid={BANKING_OFFER_MODULE_TEST_ID.checkbox}
                  onChange={({ currentTarget }) => setIsChecked(currentTarget.checked)}
                  className={styles.checkbox}
                />
              }
              label={checkboxLabel}
            />
          </FormControl>
          <ButtonLinkV2
            isFullWidth
            aria-disabled={!isChecked}
            data-testid={BANKING_OFFER_MODULE_TEST_ID.submitButton}
            entry={toEntry(link)}
          />
        </div>
        <TextV2
          className={styles.feesInformation}
          variant="small"
          tagName="div"
          value={feesInformation}
          isMarkdown
        />
      </section>
      <StructuredModal
        isOpen={hasFailedFetchingMerchantProspectData}
        onRequestClose={() => closeModalAndRedirect()}
        className={styles.modal}
        shouldCloseOnEsc
        title={t('fiErrorModal.title')}
        description={t('fiErrorModal.subtitle')}
        actions={[
          {
            label: t('close'),
            variant: LinkVariant.PRIMARY,
            action: () => closeModalAndRedirect(),
          },
        ]}
      />
    </>
  );
};

export default BankingOfferModule;
